import { useMemo } from "react";
import { Item } from "@/dto/item";
import { CategoryType } from "@/types";

interface UseMenuSearchParams {
  items: Item[] | undefined | null;
  query: string | string[] | undefined | null;
  type?: CategoryType;
}

/**
 * A hook to filter menu items based on a search query.
 *
 * It handles:
 * - Normalizing router queries (string | string[])
 * - creating an ID-based map for easy lookups
 * - Case-insensitive name filtering
 * - Empty/Null states safely
 *
 * @param params - The input parameters containing items and the query.
 * @returns The search count, the filtered list, and the ID map.
 *
 * @example
 * const { searchItemCount, filteredItems } = useMenuSearch({
 * items: menuData,
 * query: router.query.search
 * });
 */
export const useMenuSearch = ({ items, query, type }: UseMenuSearchParams) => {
  const normalizedQuery = useMemo(() => {
    if (!query) return "";
    return Array.isArray(query) ? query[0] : query;
  }, [query]);

  const itemsMap = useMemo(() => {
    if (!items || items.length === 0) return null;

    return Object.fromEntries(items.map((item) => [item.id, item])) as Record<
      string,
      Item
    >;
  }, [items]);

  const filteredItems = useMemo(() => {
    if (!itemsMap) return [];

    const allItems = Object.values(itemsMap);
    const lowerQuery = normalizedQuery.toLowerCase();

    return allItems.filter((item) => {
      // Base name search (common to all)
      const matchesName =
        !lowerQuery || item.name?.toLowerCase().includes(lowerQuery);

      switch (type) {
        case "REGULAR":
          return matchesName && item.itemType === "REGULAR";
        case "SERVICE":
          return matchesName && item.itemType === "SERVICE";
        default:
          return matchesName;
      }
    });
  }, [itemsMap, normalizedQuery, type]);

  return {
    searchItemCount: filteredItems.length,
    itemsMap,
    filteredItems,
  };
};
